// Siparis: aliciya ornek fotograf. Acik / koyu tasarim ya da DISARIDAN yuklenen bir tasarim,
// secilen mockup'a sunucuda basilir (mockup'in golge, kirisik, doku ayarlariyla) ve indirilir.
// Sunucu: POST /api/mockups/render — light/dark ikisine de secilen dosya verilir ki mockup
// alaninin temasi ne olursa olsun secilen tasarim basilsin.

import { useEffect, useState } from "react";
import { Download, ImagePlus, Images, Loader2, Upload, X } from "lucide-react";
import { clsx } from "clsx";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { MockupChooserModal, type ChooserMockup } from "../mockup/MockupChooserModal";
import type { LinkedDesign } from "./OrderDesignFiles";

const abs = (u?: string | null) =>
  !u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`;

type Source = { key: string; label: string; url: string };

function sourcesOf(designs: LinkedDesign[]): Source[] {
  const out: Source[] = [];
  for (const d of designs) {
    const main = d.editedUrl || d.originalUrl;
    const name = designs.length > 1 ? `${d.sku || d.name} · ` : "";
    if (main) out.push({ key: `${d.id}:main`, label: `${name}${d.theme === "dark" ? "Koyu zemin" : d.theme === "light" ? "Açık zemin" : "Ana tasarım"}`, url: main });
    if (d.variantUrl) out.push({ key: `${d.id}:variant`, label: `${name}${d.variantTheme === "dark" ? "Koyu zemin" : d.variantTheme === "light" ? "Açık zemin" : "İkinci tasarım"}`, url: d.variantUrl });
  }
  return out;
}

export function OrderSampleMockup({ designs, sku }: { designs: LinkedDesign[]; sku?: string | null }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] text-emerald-700 hover:bg-emerald-100"
        title="Tasarımı bir mockup'a basıp alıcıya örnek fotoğraf olarak indir"
      >
        <Images className="h-3 w-3" /> Örnek mockup
      </button>
      {open && <SampleModal designs={designs} sku={sku} onClose={() => setOpen(false)} />}
    </>
  );
}

function SampleModal({ designs, sku, onClose }: { designs: LinkedDesign[]; sku?: string | null; onClose: () => void }) {
  const sources = sourcesOf(designs);
  const [pick, setPick] = useState<string>(sources[0]?.key ?? "upload");
  const [upload, setUpload] = useState<{ name: string; dataUrl: string } | null>(null);
  const [mockup, setMockup] = useState<ChooserMockup | null>(null);
  const [chooser, setChooser] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !chooser) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chooser, onClose]);

  const designUrl = pick === "upload" ? upload?.dataUrl ?? null : sources.find((s) => s.key === pick)?.url ?? null;

  const onFile = (f: File | undefined) => {
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { setUpload({ name: f.name, dataUrl: String(r.result) }); setPick("upload"); setResult(null); };
    r.readAsDataURL(f);
  };

  const render = async () => {
    if (!designUrl || !mockup) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await fetch(`${getBackendUrl()}/api/mockups/render`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ light: designUrl, dark: designUrl, mockupIds: [mockup.id] }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Mockup oluşturulamadı");
      const img = j.data?.images?.[0]?.url as string | undefined;
      if (!img) throw new Error(j.data?.failed?.[0]?.reason || "Mockup oluşturulamadı (baskı alanı tanımlı mı?)");
      setResult(img);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!result) return;
    const blob = await (await fetch(abs(result))).blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${(sku || "ornek").replace(/[^\w.-]+/g, "_")}-ornek-mockup.jpg`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900"><Images className="h-5 w-5 text-indigo-600" /> Örnek mockup</h2>
          <button onClick={onClose} aria-label="Kapat" title="Kapat" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="grid gap-5 overflow-y-auto p-5 md:grid-cols-[16rem_1fr]">
          <div className="space-y-4">
            <div>
              <p className="mb-1 block text-xs font-medium text-gray-700">Tasarım</p>
              <div className="space-y-1.5">
                {sources.map((s) => (
                  <label key={s.key} className={clsx("flex cursor-pointer items-center gap-2 rounded-lg border p-1.5 text-sm", pick === s.key ? "border-indigo-300 bg-indigo-50" : "border-gray-200 hover:bg-gray-50")}>
                    <input type="radio" name="sample-src" checked={pick === s.key} onChange={() => { setPick(s.key); setResult(null); }} />
                    <span className="bg-alpha-check h-9 w-9 shrink-0 overflow-hidden rounded"><img src={abs(s.url)} alt="" className="h-full w-full object-contain" /></span>
                    <span className="truncate text-gray-700">{s.label}</span>
                  </label>
                ))}
                <label className={clsx("flex cursor-pointer items-center gap-2 rounded-lg border p-1.5 text-sm", pick === "upload" ? "border-indigo-300 bg-indigo-50" : "border-gray-200 hover:bg-gray-50")}>
                  <input type="radio" name="sample-src" checked={pick === "upload"} onChange={() => { setPick("upload"); setResult(null); }} />
                  {upload ? (
                    <span className="bg-alpha-check h-9 w-9 shrink-0 overflow-hidden rounded"><img src={upload.dataUrl} alt="" className="h-full w-full object-contain" /></span>
                  ) : <Upload className="h-4 w-4 text-gray-400" />}
                  <span className="min-w-0 flex-1 truncate text-gray-700">{upload ? upload.name : "Dışarıdan tasarım yükle"}</span>
                  <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">Şeffaf arka planlı PNG en iyi sonucu verir.</p>
            </div>

            <div>
              <p className="mb-1 block text-xs font-medium text-gray-700">Mockup</p>
              <button onClick={() => setChooser(true)} className="flex w-full items-center gap-2 rounded-lg border border-gray-200 p-1.5 text-left text-sm hover:bg-gray-50">
                {mockup ? (
                  <>
                    <img src={abs(mockup.thumbnailUrl || mockup.imageUrl)} alt="" className="h-12 w-12 shrink-0 rounded object-cover" />
                    <span className="truncate text-gray-700">{mockup.label}</span>
                  </>
                ) : (
                  <><ImagePlus className="h-4 w-4 text-gray-400" /><span className="text-gray-500">Mockup seç</span></>
                )}
              </button>
              <p className="mt-1 text-xs text-gray-500">Mockup'ın gölge, kırışık ve doku ayarları uygulanır.</p>
            </div>
          </div>

          <div className="flex min-h-[18rem] items-center justify-center rounded-xl border border-gray-200 bg-gray-50">
            {busy ? (
              <span className="flex items-center gap-2 text-sm text-gray-600"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /> Mockup oluşturuluyor…</span>
            ) : result ? (
              <img src={abs(result)} alt="Örnek mockup" className="max-h-[60vh] w-full rounded-xl object-contain" />
            ) : (
              <span className="px-6 text-center text-sm text-gray-500">Tasarımı ve mockup'ı seçip "Mockup oluştur"a bas.</span>
            )}
          </div>
        </div>

        {err && <p className="mx-5 mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {result && (
            <button onClick={() => void download()} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
              <Download className="h-4 w-4" /> İndir
            </button>
          )}
          <button onClick={() => void render()} disabled={!designUrl || !mockup || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Images className="h-4 w-4" />} Mockup oluştur
          </button>
        </div>
      </div>

      {chooser && (
        <div className="z-[60]" onClick={(e) => e.stopPropagation()}>
          <MockupChooserModal title="Örnek için mockup seç" mode="single" newestFirst
            onPick={(m) => { setMockup(m); setResult(null); setChooser(false); }} onClose={() => setChooser(false)} />
        </div>
      )}
    </div>
  );
}
